<template>
  <div class="mb-4">
    <vs-divider class=""></vs-divider> 
    <div>
      <div class="patient-box mb-4" :key="patientIndex" v-for="(item, patientIndex) in selectedPatients">
          <div class="flex justify-between w-full items-center">
            <p class="text-bsae font-semibold">{{ item.patient.fullName }} <span class="text-muted font-normal">{{ item.patient.contactNumber }}</span></p>
            <div
              class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-opacity-75"
              @click="handleRemovePatient(patientIndex)"
            >
              <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" class="text-red-700" />
            </div>
          </div>
          <vs-alert v-if="item.isCartExisting" class="relative" color="warning" active="true" icon="info">
            <p class="font-normal text-sm">
              This patient already exists in the cart and cannot be added again unless removed first.
            </p>
          </vs-alert>
          <vs-divider class="m-0"></vs-divider> 
          <div class="flex flex-col gap-3">
            <p class="text-sm text-muted">Address</p>
            <p class="text-base text-standard-primary">{{ item.patient.address }}</p>
            <p  class="text-sm text-muted">Date of Birth</p>
            <p class="text-base text-standard-primary">{{ new Date(item.patient.dateOfBirth).toLocaleDateString('en-GB').replace(/\//g, ' / ') }}</p>
          </div>
          <vs-divider class="m-0"></vs-divider> 
          <template v-if="item.scripts.length">
            <div class="w-full" :key="scriptIndex" v-for="(script, scriptIndex) in item.scripts">
              <div class="flex flex-col gap-3 text-standard-primary text-base font-normal">
                <div class="flex justify-between items-center">
                  <div class="flex flex-col gap-3 mb-2">
                    <p class="text-base text-information font-semibold">TX - {{ script.authorisationNumber }}</p>
                    <p class="text-sm text-muted">Treatment plans - <span class="font-bold">(Total of {{ script.totalAmount }}{{ script.productUnit }})</span></p>
                    <p v-if="productVial <= script.totalAmount" class="text-sm">Repeats remaining - <span class="font-semibold">{{ script.repeatsLeft }}</span></p>
                  </div>
                  <div class="product-actions" v-if="productVial <= script.totalAmount && script.repeatsLeft > 0 && !item.isCartExisting">
                        <span class="less-btn hover:bg-gray-100" @click="handleScriptQuantity(script, -1)">
                          <vs-icon
                            size="20px"
                            class="cursor-pointer"
                            icon="remove"
                          >
                          </vs-icon>
                        </span>
                        <span class="qty-display text-new-primary text-base">{{ script.quantity }}</span>
                        <span class="add-btn hover:bg-gray-100" @click="handleScriptQuantity(script, 1)">
                          <vs-icon
                            size="20px"
                            class="cursor-pointer"
                            icon="add"
                          >
                          </vs-icon>
                        </span>
                  </div>
                </div>
                <vs-alert v-if="script.repeatsLeft == 0" class="relative" color="warning" active="true" icon="info">
                  <p class="font-normal text-sm">
                    This product cannot be purchased because it has already been used 5 times.
                  </p>
                </vs-alert>
                <vs-alert v-if="productVial > script.totalAmount" class="relative" color="warning" active="true" icon="info">
                  <p class="font-normal text-sm">
                    This product cannot be purchased as the amount authorised is less than the vial size.
                  </p>
                </vs-alert>
              </div>
            </div>
          </template>
          <vs-alert v-else class="relative" color="warning" active="true" icon="info">
            <p class="font-normal">No active scripts found for this patient at this clinic location.</p>
          </vs-alert>
      </div>
      <label for="clinic" class="block text-base font-semibold text-gray-800 mt-6 mb-1">
        Add a patient for dispense request
      </label>
      <vs-select
        class="w-full"
        autocomplete
        v-model="patientId"
        @click="getPatientsByClinic"
        @change="handlePatientOnSelect"
        placeholder="Search patient using first name, last name or phone number"
        >
        <vs-select-item :key="index" :value="item.value" :text="`${item.text} - ${item.contactNumber}`" v-for="(item,index) in unselectedPatients" />
      </vs-select>
    </div>
    <div class="space-y-4 animate-pulse mt-6" v-if="isLoadingPatientScript || isLoadingPatients">
      <div v-for="item in 3" :key="item" class="h-10 rounded-lg bg-gray-200 w-full"></div>
    </div>
    <template v-if="totalQuantity > 0">
      <vs-divider ></vs-divider> 
      <div class="w-full mx-auto p-4 bg-white shadow rounded">
        <h2 class="text-lg font-semibold mb-4">Order Summary</h2>

        <div class="flex justify-between items-center py-2 border-b">
          <div class="text-base font-medium">{{ productData.name }}</div>
          <div class="flex items-center gap-4">
            <span class="text-sm text-gray-600">Qty: {{ totalQuantity }}</span>
            <span class="text-sm font-semibold">${{ totalPrice }}</span>
          </div>
        </div>

        <div class="flex justify-between items-center mt-4 pt-2 border-t font-bold text-lg">
          <span>Total</span>
          <span>${{ totalPrice }}</span>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import { formatPriceValue } from '../../../utils/format';
import { currentUserIsSuperAdmin } from '@/globalHelpers/userRole';

export default {
  name: 'PatientForm',
  props: ['directionForUse', 'productData', 'productPrice', 'compundedOrders'],
  data() {
      return {
        isLoadingPatients:false,
        isLoadingPatientScript:false,
        organizationId: null,
        patientsList: [],
        selectedPatients:[],
        patientId: '',
        clinicId: null,
        selectedPatientIndexToDelete:null,
      };
  },
  watch: {
      '$store.state.organization.selectedOrganization'(newVal) {
        this.organizationId = newVal;
      },
      '$store.state.clinicId'(val){
        this.clinicId = val;
        this.selectedPatients = [];
      },
      totalQuantity(value) {
        this.$emit('quantityChange', value);
      },
      selectedProductsWithQuanity: {
        handler(value) {
          if (!value.length) return;
          this.$emit('compoundedOrderChange', value);
        },
        deep: true,
      },
      compundedOrders(value) {
        if (!value.length) {
          this.selectedPatients = [];
        }
      },
  },
  computed: {
    isUSRegion: () => process.env.VUE_APP_REGION !== 'AU',
    unselectedPatients() {
      const patients =  this.patientsList.filter((patient) => {
        return !this.selectedPatients.some((selectedPatient) => selectedPatient.patient._id === patient._id);
      });

      return patients;
    },
    productVial() {
      return this.productData.amount || 0;
    },
    totalQuantity() {
      return this.selectedPatients.reduce((patientAcc, patientObj) => {
        const scriptsQuantity = patientObj.scripts.reduce((scriptAcc, script) => {
          // Sum script quantity
          const treatmentQuantity = script.treatmentPlans.reduce((planAcc, plan) => {
            return planAcc + (plan.quantity || 0);
          }, 0);

          return scriptAcc + (script.quantity || 0) + treatmentQuantity;
        }, 0);

        return patientAcc + scriptsQuantity;
      }, 0);
    },
    cartItems() {
      return this.$store.state.eCommerce.cartItems.slice().reverse();
    },
    totalPrice() {
      return formatPriceValue(this.totalQuantity * (this.productPrice * 100));
    },
    selectedProductsWithQuanity() {
      const allScripts = this.selectedPatients.flatMap(selectedPatient =>
        selectedPatient.scripts
          .filter(script => script.quantity > 0)
          .map(script => ({
            ...script,
            patientId: selectedPatient.patient._id,
            patientName: selectedPatient.patient.fullName,
            patientFirstName: selectedPatient.patient.firstName,
            patientLastName: selectedPatient.patient.lastName,
            dateOfBirth: selectedPatient.patient.dateOfBirth,
            address: selectedPatient.patient.address,
            allergies: selectedPatient.patient.allergies,
            contact: selectedPatient.patient.contactNumber,
            email: selectedPatient.patient.email,
          }))
      );

      const grouped = {};

      for (const script of allScripts) {
        const {
          patientId,
          patientName,
          patientFirstName,
          patientLastName,
          address,
          dateOfBirth,
          allergies,
          contact,
          email,
          authorisationId,
          pdfLink,
          doctorId,
          quantity,
        } = script;

        if (!grouped[patientId]) {
          grouped[patientId] = {
            patientId,
            patientName,
            patientFirstName,
            patientLastName,
            allergies: Array.isArray(allergies)
                    ? allergies.join(', ')
                    : allergies || '',
            contact,
            email,
            address,
            dateOfBirth,
            productStoreId: this.productData._id,
            supplierId: this.productData.supplier._id,
            scripts: [],
          };
        }

        grouped[patientId].scripts.push({
          quantity,
          scriptId: authorisationId,
          doctorId,
          pdfLink,
        });
      }

      return Object.values(grouped);
    },
  },
  created() {
    if (process.env.VUE_APP_REGION !== 'AU') {
        this.inputs.push(
            {
                label: 'Allergies',
                name: 'allergies',
                rule: 'patient.allergies',
            }
        );
    }
  },
  mounted() {
    this.clinicId = sessionStorage.getItem('doctorClinicSelectedClinic');
  },
  methods: {
    ...mapActions('orgOwner', ['getPatients']),
    ...mapActions('compoundedOrder', ['getPatientScripts']),
    async getPatientsByClinic() {
      if (currentUserIsSuperAdmin() && !this.organizationId) {
        this.$vs.notify({
          title: 'Unable to Fetch Treatment Authorities',
          text: 'We couldn’t load the patient list. Please select an organization or try again later.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
        });
        return;
      }

      if (this.patientsList.length) return;

      this.isLoadingPatients = true;

      try {
        const res = await this.getPatients({
          id: this.organizationId,
          limit: 999999,
          productId: this.productData._id,
        });

        this.patientsList = res.data.data.docs.map((patient) => ({
          value: patient._id,
          text: patient.fullName,
          ...patient,
        }));
      } catch (err) {
        this.$vs.notify({
          title: 'Failed Fetching Patient Treatment Authorities',
          text: 'An error occurred while fetching the patients. Please try again later.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
        });
      } finally {
        this.isLoadingPatients = false;
      }
    },
    handleScriptQuantity(script, change) {
      const newQuantity = script.quantity + change;

      if (newQuantity < 0) return;

      // Check if the new quantity exceeds the maximum vial limit
      if (newQuantity * this.productVial > script.totalAmount) {
        this.$vs.notify({
          title: 'Maximum Vial Reached',
          text: 'You have reached the maximum quantity allowed for this vial.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning',
        });
        return;
      }

      script.quantity = newQuantity;
    },
    handleRemovePatient(patientIndex) {
      this.selectedPatientIndexToDelete = patientIndex;
      this.selectedPatients.splice(patientIndex, 1);
    },
    isCartPatientExisting(patientId){
      const cartItem = this.cartItems.find((item) => item.compoundedOrder && item.compoundedOrder.patientId == patientId);

      if (!cartItem) return false;

      return true;
    },
    async handlePatientOnSelect() {
      if (!this.patientId) return;

      this.isLoadingPatientScript = true;

      try {
        const { data } = await this.getPatientScripts({ patientId:this.patientId, clinicId: this.clinicId, productId: this.productData._id });
        const { scripts } = data.data;
        const patient = this.patientsList.find(({ _id }) => _id === this.patientId);
        const formattedScripts = scripts.map((script) => {
          return {
            ...script,
            quantity: 0,
            treatmentPlans: script.treatmentPlans.map((plan) => {
              return {
                ...plan,
              };
            }),
          };
        });

        this.selectedPatients.push({
          patient,
          scripts: formattedScripts,
          show_details: false,
          isCartExisting: this.isCartPatientExisting(this.patientId),
        });

        this.patientId = '';
      } catch (error) {
        this.patientId = '';
      } finally {
        this.isLoadingPatientScript = false;
      }
    },
  },
  beforeMount: async function () {
    if (
      localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`)
    ) {
      this.organizationId = localStorage.getItem(
        `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`
      );
    }
  },
};
</script>
<style>
.custom-field label {
  display: block;
  padding: 5px 0 !important;
  font-size: 12px;
}

.bg-color-error {
  background: rgba(234, 84, 85, 0.14);
  color: rgba(234, 84, 85, 1);
}

.bg-color-done {
  background: rgba(40, 199, 111, 0.14);
  color: rgba(40, 199, 111, 1);
}

.is-danger {
  border: solid 1px rgba(234, 84, 85, 1) !important;
}

.is-true {
  border: solid 1px rgba(40, 199, 111, 1) !important;
}

.font-12 span {
  font-size: 12px !important;
}

.patient-box {
  border-radius: 12px;
  border: 1px solid #E3DDD7;
  background: #FFF;
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

.less-btn, .qty-display, .add-btn {
  border: 1px solid #969696;
  display: flex;
  width: 48px;
  height: 48px;
  padding: 0px;
  justify-content: center;
  align-items: center;
  gap: 0px;
  background: #FFF;
}

.less-btn {
  border-radius: 8px 0px 0px 8px;
  border-right: none;
}

.add-btn {
  border-radius: 0px 8px 8px 0px;
  border-left: none;
}

.product-actions {
  display: flex;
  flex-direction: row;
}

.text-muted {
  color: #828282;
}

.text-new-primary {
  color: #232323;
}

.text-standard-primary {
  color: #262928
}

.text-information {
  color: #125277;
}

.text-disabled {
  color: #969696;
}
</style>